import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { Button, ResourceList } from 'ui';
import { Segment, Segments } from 'ui/molecules/SegmentedControl';
import { ResourceDetailModal } from 'ui/molecules/ResourceDetailModal';
import { sortArrayByProperty } from 'utils/sort-methods';
import { ResourceSelectionType } from 'utils/file-utils';

import { ReactComponent as DeleteIcon } from './delete.svg';
import './file-selector.css';

export const setAllSelectedItems = ({
  items,
  resourceList,
  setSelectedFiles,
  setSelectedIndexes,
}) => {
  const filesIdsToAdd = [] as string[];
  const indexIdsToAdd = [] as string[];
  for (const item of items) {
    const fullItem = resourceList.find(
      (r: ResourceSelectionType) => r.id === item,
    );
    if (fullItem.itemType === 'index') {
      indexIdsToAdd.push(item);
    } else {
      filesIdsToAdd.push(item);
    }
  }
  setSelectedFiles(filesIdsToAdd);
  setSelectedIndexes(indexIdsToAdd);
};

const FileSelector = ({
  resourceList,
  selectedFileIds,
  selectedIndexIds,
  currentConversationSelectedFileIds,
  currentConversationUsedFileIds,
  setSelectedFiles,
  setSelectedIndexes,
  setConfirmDelete,
  setFileSelectorVisible,
}) => {
  const [localError, setLocalError] = useState<string>('');
  const [segments, setSegments] = useState<Segment[] | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(2);
  const [showResourceDetailModal, setShowResourceDetailModal] = useState(false);
  const [currentViewableResource, setCurrentViewableResource] = useState({});

  const [
    currentConversationSelectedFiles,
    setCurrentConversationSelectedFiles,
  ] = useState<any[]>([]);

  useEffect(() => {
    setCurrentConversationSelectedFiles(
      resourceList.filter(
        (resource: ResourceSelectionType) =>
          currentConversationSelectedFileIds.includes(resource.id) &&
          resource.processingStatus === 'done',
      ),
    );
  }, [resourceList, currentConversationSelectedFileIds]);

  useEffect(() => {
    setSegments([
      {
        label: 'All',
        index: 0,
        active: currentIndex === 0,
        children: (
          <ResourceList
            availableToSelectItems={resourceList
              .filter((file) => file.processingStatus !== 'deleted')
              .filter(
                (r: ResourceSelectionType) =>
                  !currentConversationUsedFileIds.includes(r.id),
              )
              .sort((a, b) => sortArrayByProperty(a, b, 'displayFileName'))}
            setSelectedItems={(items) =>
              setAllSelectedItems({
                items,
                resourceList,
                setSelectedFiles,
                setSelectedIndexes,
              })
            }
            selectedItemIds={[...selectedFileIds, ...selectedIndexIds]}
            setError={function (error: string): void {
              setLocalError(error);
            }}
            setShowResourceDetailModal={setShowResourceDetailModal}
            setCurrentViewableResource={setCurrentViewableResource}
            className="resourcelist-all"
            showSelectedAll={false}
          />
        ),
      },
      {
        label: 'Files',
        active: currentIndex === 1,
        index: 1,
        children: (
          <ResourceList
            availableToSelectItems={resourceList
              .filter((file) => file.processingStatus !== 'deleted')
              .filter((r) => !currentConversationUsedFileIds.includes(r.id))
              .filter((r) => r.itemType === 'file')
              .sort((a, b) => sortArrayByProperty(a, b, 'displayFileName'))}
            setSelectedItems={function (items: any[]): void {
              setSelectedFiles(items);
            }}
            selectedItemIds={currentConversationSelectedFileIds}
            setError={function (error: string): void {
              setLocalError(error);
            }}
            showSelectedAll={false}
            className="resourcelist-files"
          />
        ),
      },
      {
        label: 'Indexes',
        active: currentIndex === 2,
        index: 2,
        children: (
          <ResourceList
            availableToSelectItems={resourceList
              .filter((r) => !currentConversationUsedFileIds.includes(r.id))
              .filter((r) => r.itemType === 'index')
              .sort((a, b) => sortArrayByProperty(a, b, 'displayFileName'))}
            setSelectedItems={function (items: any[]): void {
              setSelectedIndexes(items);
            }}
            selectedItemIds={selectedIndexIds}
            setError={function (error: string): void {
              setLocalError(error);
            }}
            showSelectedAll={false}
            setShowResourceDetailModal={setShowResourceDetailModal}
            setCurrentViewableResource={setCurrentViewableResource}
            className="resourcelist-indexes"
          />
        ),
      },
      {
        label: 'Applied',
        active: currentIndex === 3,
        index: 3,
        children: (
          <ResourceList
            type="selected"
            availableToSelectItems={resourceList.filter((r) =>
              currentConversationUsedFileIds.includes(r.id),
            )}
            setSelectedItems={function (): void {
              throw new Error('Function not implemented.');
            }}
            selectedItemIds={resourceList
              .filter((r) => r.isSelected)
              .map((r) => r.id)}
            setError={function (error: string): void {
              setLocalError(error);
            }}
            title=""
            className="files-applied"
            showSelectedAll={false}
            hideCheckbox={true}
          />
        ),
      },
    ]);
  }, [
    currentConversationSelectedFileIds,
    currentConversationSelectedFiles,
    currentConversationUsedFileIds,
    currentIndex,
    localError,
    resourceList,
    selectedFileIds,
    selectedIndexIds,
    setSelectedFiles,
    setSelectedIndexes,
  ]);

  return (
    <div
      className={classNames('file-selector', {
        'has-files-applied': currentConversationSelectedFiles.length > 0,
      })}
      data-testid="test-file-selector"
    >
      {localError && <div className="error">{localError}</div>}
      {segments && (
        <Segments
          segments={segments}
          toggleActive={(label) => {
            const newIndex = segments.findIndex(
              (segment) => segment.label === label,
            );
            setCurrentIndex(newIndex);
          }}
          setCurrentIndex={setCurrentIndex}
          currentIndex={currentIndex}
        />
      )}

      <div className="file-list-active-wrapper">
        {segments?.find((s) => s.active)?.children}
      </div>

      {resourceList.length > 0 && (
        <Button
          type="link"
          aria-label="Delete File(s)"
          data-testid="delete-file-button"
          className="ant-modal-round-button delete-button"
          icon={<DeleteIcon />}
          onClick={() => {
            if (selectedFileIds.length > 0) {
              setConfirmDelete(true);
            }
          }}
        />
      )}
      <ResourceDetailModal
        resource={currentViewableResource}
        showResourceDetailModal={showResourceDetailModal}
        setShowResourceDetailModal={setShowResourceDetailModal}
        setFileSelectorVisible={setFileSelectorVisible}
      />
    </div>
  );
};

export default FileSelector;
